
import { computed, defineComponent, ref, Ref, watch } from 'vue';
import navigationStore from '@/shared/navigation/navigation.store';
import MainNavigation from '@/shared/navigation/site-navigation/MainNavigation.vue';
import MainNavigationMobile from '@/shared/navigation/site-navigation/MainNavigationMobile.vue';
import AnimateValue from '@/core/ui-components/AnimateValue.vue';
import SearchArea from '@/shared/search/ui/SearchArea.vue';
import MiniBasketArea from '@/b2b/shared/checkout/ui/MiniBasketArea.vue';
import useSearch from '@/shared/search/useSearch';

export default defineComponent({
    name: 'MainNavigationHandler',
    components: { MiniBasketArea, SearchArea, AnimateValue, MainNavigation, MainNavigationMobile },
    setup() {
        const { pending } = useSearch();

        const mainNavigation: Ref<null | Element> = ref(null);
        const mainNavigationWrapper: Ref<null | Element> = ref(null);
        const isOverflowEnabled = ref(false);

        const activeMainNavigationTransitionKey = computed(() => {
            return navigationStore.activeMainNavigationIdentifier;
        });

        const mainNavigationComponent = computed(() => {
            return navigationStore.activeMainNavigationComponent;
        });

        const activeMainNavigationId = computed((): MainNavigationID => {
            if (navigationStore.isMobileNavigation) {
                return 'mobile-menu';
            }
            if (navigationStore.hasActiveSearch) {
                return 'live-search';
            }
            return navigationStore.activeMainNavigationItem ? navigationStore.activeMainNavigationItem!.id : 0;
        });

        const searchResultPending = computed(() => {
            return pending.value;
        });

        const enter = (el: HTMLElement): void => {
            setHeight();
            isOverflowEnabled.value = false;
        };

        const afterEnter = (): void => {
            isOverflowEnabled.value = true;
        };

        const beforeLeave = (): void => {
            isOverflowEnabled.value = false;
        };

        const setHeight = (): void => {
            if (navigationStore.isMobileNavigation) {
                return;
            }
            const wrap = mainNavigationWrapper.value as HTMLElement;
            const element = mainNavigation.value as HTMLElement;

            wrap.style.height = `${element.offsetHeight}px`;
        };

        watch(searchResultPending, (isPending, wasPending) => {
            const resultUpdated = wasPending && !isPending;
            if (resultUpdated) {
                setTimeout(setHeight, 200);
            }
        });

        return {
            isOverflowEnabled,
            activeMainNavigationTransitionKey,
            activeMainNavigationId,
            mainNavigationComponent,
            mainNavigation,
            mainNavigationWrapper,
            enter,
            afterEnter,
            beforeLeave,
        };
    },
});

type MainNavigationID = 'mobile-menu' | 'live-search' | number;
